// API scope resolver. The same api/*.ts functions target one of three URL
// trees depending on the active profile:
//   admin   → /admin/...
//   manager → /managers/{managerDetailId}/...    (branch_managers pivot scope)
//   trainer → /trainers/{trainerDetailId}/...    (batch_trainers pivot scope)
// The manager/trainer prefixes mirror the backend route files loaded under
// `api/managers` and `api/trainers` (RouteServiceProvider), and the mobile app.
// All manager/trainer data is scoped server-side via ensure_manager_*/
// ensure_trainer_* middleware, so the client never sends assignment filters
// for security — it only resolves the right base path here.
import { authStore } from '@/store/auth';
import type { Profile } from '@/api/types/common';

// Roles the web console can actually render. Student profiles exist on the same
// login but have no web UI, so they are never selected as the active profile.
// Shared by the login flow, profile picker, and Switch Account dialog.
export const WEB_ROLES = new Set<Profile['role']>(['admin', 'manager', 'trainer']);

export function isWebRole(role: Profile['role'] | undefined): boolean {
  return role !== undefined && WEB_ROLES.has(role);
}

export type Scope =
  | { role: 'admin'; base: '/admin' }
  | { role: 'manager'; base: string; managerId: number } // base = `/managers/${managerId}`
  | { role: 'trainer'; base: string; trainerId: number }; // base = `/trainers/${trainerId}`

/**
 * Resolve the API base path for the currently-selected profile.
 * Manager → `/managers/{manager_detail id}`, Trainer → `/trainers/{trainer_detail id}`
 * (both from profile.detail.id). Everyone else (admin, or a missing/invalid
 * role detail) falls back to `/admin`.
 */
export function activeScope(): Scope {
  const p = authStore.getState().profile;
  if (p?.role === 'manager' && typeof p.detail?.id === 'number') {
    return { role: 'manager', base: `/managers/${p.detail.id}`, managerId: p.detail.id };
  }
  if (p?.role === 'trainer' && typeof p.detail?.id === 'number') {
    return { role: 'trainer', base: `/trainers/${p.detail.id}`, trainerId: p.detail.id };
  }
  return { role: 'admin', base: '/admin' };
}

export function isAdminScope(): boolean {
  return activeScope().role === 'admin';
}

export function isManagerScope(): boolean {
  return activeScope().role === 'manager';
}

export function isTrainerScope(): boolean {
  return activeScope().role === 'trainer';
}
